import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { trpc } from '@/api/trpc';

interface AutopilotPlansListProps {
    onSelectPlan: (planId: number) => void;
    onNewAutopilot: () => void;
}

const STATUS_DOT_CLASS: Record<string, string> = {
    running: 'bg-green-500',
    draft: 'bg-yellow-500',
    locked: 'bg-blue-500',
    completed: 'bg-green-400',
    failed: 'bg-red-500',
    paused: 'bg-zinc-400',
    canceled: 'bg-zinc-500',
    archived: 'bg-zinc-600'
};

const STATUS_SORT_ORDER: Record<string, number> = {
    running: 0,
    draft: 1,
    locked: 2,
    completed: 3,
    failed: 4,
    paused: 5,
    canceled: 6,
    archived: 7
};

export function AutopilotPlansList({ onSelectPlan, onNewAutopilot }: AutopilotPlansListProps) {
    const plansQuery = useQuery({
        ...trpc.agent.autopilot.listPlans.queryOptions({ limit: 30 }),
        retry: false
    });

    const plans = plansQuery.data?.plans ?? [];

    const sortedPlans = useMemo(() => {
        return [...plans].sort((a, b) => {
            const orderA = STATUS_SORT_ORDER[a.status] ?? 99;
            const orderB = STATUS_SORT_ORDER[b.status] ?? 99;
            if (orderA !== orderB) return orderA - orderB;
            return b.updatedAt.localeCompare(a.updatedAt);
        });
    }, [plans]);

    return (
        <div className="border-t border-zinc-800">
            <div className="flex items-center justify-between px-3 py-2">
                <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Autopilot Plans</span>
                    {plans.length > 0 && (
                        <span className="text-[10px] bg-zinc-700 text-zinc-300 rounded-full px-1.5 py-0.5 font-medium">{plans.length}</span>
                    )}
                </div>
                <button
                    className="text-[10px] px-1.5 py-0.5 rounded bg-fuchsia-700 hover:bg-fuchsia-600 text-white font-medium"
                    onClick={onNewAutopilot}
                    type="button"
                >
                    + New
                </button>
            </div>

            {plansQuery.isLoading && <div className="px-3 pb-2 text-xs text-zinc-500">Loading plans...</div>}

            {sortedPlans.length === 0 && !plansQuery.isLoading && <div className="px-3 pb-2 text-xs text-zinc-500">No plans yet.</div>}

            {sortedPlans.map((plan) => {
                const timeAgo = plan.updatedAt ? dayjs(plan.updatedAt).fromNow() : '';
                const dotClass = STATUS_DOT_CLASS[plan.status] ?? 'bg-zinc-600';

                return (
                    <button
                        className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-zinc-800/40 border-b border-zinc-800/50 last:border-b-0"
                        key={plan.id}
                        onClick={() => onSelectPlan(plan.id)}
                        type="button"
                    >
                        <span className={`inline-block h-2 w-2 rounded-full shrink-0 ${dotClass}`} />
                        <span className="flex-1 min-w-0 truncate text-xs text-zinc-200">{plan.name}</span>
                        <span className="text-[10px] text-zinc-500 shrink-0">{timeAgo}</span>
                    </button>
                );
            })}
        </div>
    );
}
